// apps/web/src/lib/estoque/teclado-peso.ts — Fase 13, Plano 30 (EST-15, D-05, D-28, T-13-30C).
// Regras PURAS do teclado numérico próprio da pesagem de tinta no tablet: vírgula única, até 4 casas, apagar e limpar.
// O servidor revalida (maxDecimalPlaces 4, peso > 0); aqui só se evita o round-trip óbvio e o teclado do sistema.

export const MAX_CASAS_PESO = 4;
/** Evita um peso absurdo por toque repetido; o servidor continua sendo a fonte da verdade. */
export const MAX_DIGITOS_INTEIROS_PESO = 6;

export const TECLA_VIRGULA = ',';
export const TECLA_APAGAR = 'APAGAR';
export const TECLA_LIMPAR = 'LIMPAR';

export const TECLAS_PESO = ['1', '2', '3', '4', '5', '6', '7', '8', '9', TECLA_VIRGULA, '0', TECLA_APAGAR] as const;

export const MENSAGEM_PESO_ZERO = 'Informe um peso maior que zero.';
export const MENSAGEM_PESO_CASAS = 'Use até 4 casas decimais.';

/** Aplica uma tecla ao texto do peso; teclas que violariam a regra (2ª vírgula, 5ª casa) são ignoradas. */
export function aplicarTecla(texto: string, tecla: string): string {
  if (tecla === TECLA_LIMPAR) return '';
  if (tecla === TECLA_APAGAR) return texto.slice(0, -1);
  if (tecla === TECLA_VIRGULA) {
    if (texto.includes(',')) return texto;
    return texto === '' ? '0,' : `${texto},`;
  }
  if (!/^\d$/.test(tecla)) return texto;
  const [inteira = '', decimal] = texto.split(',');
  if (decimal !== undefined) {
    return decimal.length >= MAX_CASAS_PESO ? texto : `${texto}${tecla}`;
  }
  if (inteira === '0') return tecla;
  return inteira.length >= MAX_DIGITOS_INTEIROS_PESO ? texto : `${texto}${tecla}`;
}

/** Peso digitado como número (ponto decimal); null quando não há peso interpretável. */
export function pesoDoTexto(texto: string): number | null {
  const limpo = texto.endsWith(',') ? texto.slice(0, -1) : texto;
  if (limpo === '' || !/^\d+(,\d+)?$/.test(limpo)) return null;
  const numero = Number(limpo.replace(',', '.'));
  return Number.isFinite(numero) ? numero : null;
}

/** Leitura exibida no Display: o que foi digitado, ou 0 enquanto vazio. */
export function exibirPeso(texto: string): string {
  return texto === '' ? '0' : texto;
}

/** Mensagem de validação do peso (UI-SPEC) ou null quando válido. */
export function validarPeso(texto: string): string | null {
  const [, decimal] = texto.split(',');
  if (decimal !== undefined && decimal.length > MAX_CASAS_PESO) return MENSAGEM_PESO_CASAS;
  const peso = pesoDoTexto(texto);
  if (peso === null || peso <= 0) return MENSAGEM_PESO_ZERO;
  return null;
}

/** Tecla lógica do teclado físico (desktop); null para teclas que não pertencem ao teclado do peso. */
export function teclaDoEventoFisico(key: string): string | null {
  if (/^\d$/.test(key)) return key;
  if (key === ',' || key === '.') return TECLA_VIRGULA;
  if (key === 'Backspace') return TECLA_APAGAR;
  if (key === 'Delete' || key === 'Escape') return TECLA_LIMPAR;
  return null;
}

const CONTROLES_COM_ENTER_PROPRIO = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A', 'SUMMARY']);

/**
 * O Enter do teclado físico só confirma a pesagem quando o foco NÃO está num controle interativo (CR-C02): em botão, link,
 * campo ou seleção o Enter pertence ao controle (ativar "Trocar lote", abrir o motivo etc.), nunca a registrar a movimentação.
 */
export function enterConfirmaPesagem(alvo: { readonly tagName?: string; readonly isContentEditable?: boolean } | null): boolean {
  if (alvo === null) return true;
  if (alvo.isContentEditable === true) return false;
  return !CONTROLES_COM_ENTER_PROPRIO.has((alvo.tagName ?? '').toUpperCase());
}
